/**
 * Configuration Manager for Discovery Settings
 * Handles dynamic configuration without code modifications
 *
 * @module ConfigManager
 */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Discovery hub configuration
 */
interface DiscoveryHubConfig {
    url: string;
    port: number;
    timeout: number;
    retryAttempts: number;
    endpoints: {
        discoverSingle: string;
        discoverAll: string;
        dcmsDiscovery: string;
        health: string;
    };
}

/**
 * Credential configuration
 */
interface CredentialConfig {
    username: string;
    password: string;
    type: string;
}

/**
 * SSH tunnel configuration
 */
interface SSHTunnelConfig {
    enabled: boolean;
    localPort: number;
    remoteHost: string;
    remotePort: number;
}

/**
 * Proxy configuration
 */
interface ProxyConfig {
    enabled: boolean;
    port: number;
    corsEnabled: boolean;
}

/**
 * Discovery settings
 */
interface DiscoverySettings {
    defaultTimeout: number;
    detailedLogging: boolean;
    autoRetryOnFailure: boolean;
    maxDevicesPerScan: number;
}

/**
 * Complete configuration structure
 */
interface Config {
    discoveryHub: DiscoveryHubConfig;
    credentials: {
        default: CredentialConfig;
        [key: string]: CredentialConfig;
    };
    sshTunnel: SSHTunnelConfig;
    proxy: ProxyConfig;
    discovery: DiscoverySettings;
    lastUpdated: string;
    version: string;
}

/**
 * Configuration Manager class
 */
export class ConfigManager {
    private configKey: string = 'asd_discovery_config';
    private config: Config;
    private defaultConfig: Config = {
        discoveryHub: {
            url: "http://16.176.249.104",  // Direct IP for reliability (unboundata.com alias)
            port: 8080,
            timeout: 30,
            retryAttempts: 3,
            endpoints: {
                discoverSingle: "/discover/ip/",
                discoverAll: "/discover/all",
                dcmsDiscovery: "/discover/ip/",
                health: "/health"
            }
        },
        credentials: {
            default: {
                username: "ada-robot",
                password: "ASD-Discovery-2025!",
                type: "ssh-password"
            }
        },
        sshTunnel: {
            enabled: true,
            localPort: 8081,
            remoteHost: "16.176.249.104",
            remotePort: 8080
        },
        proxy: {
            enabled: false,  // Disabled since we're going direct to unboundata.com
            port: 8082,
            corsEnabled: true
        },
        discovery: {
            defaultTimeout: 30,
            detailedLogging: true,
            autoRetryOnFailure: true,
            maxDevicesPerScan: 254
        },
        lastUpdated: new Date().toISOString(),
        version: "1.0.0"
    };

    constructor() {
        // Load config on initialization
        this.config = this.loadConfig();
    }

    /**
     * Load configuration from localStorage or use defaults
     */
    private loadConfig(): Config {
        try {
            const stored = localStorage.getItem(this.configKey);
            if (stored) {
                const config = JSON.parse(stored) as Config;
                console.log('📋 Loaded configuration from localStorage:', config);
                return config;
            }
        } catch (error) {
            console.error('Error loading config:', error);
        }

        console.log('📋 Using default configuration');
        return { ...this.defaultConfig };
    }

    /**
     * Save configuration to localStorage
     */
    saveConfig(): boolean {
        try {
            this.config.lastUpdated = new Date().toISOString();
            localStorage.setItem(this.configKey, JSON.stringify(this.config));
            console.log('💾 Configuration saved to localStorage');
            return true;
        } catch (error) {
            console.error('Error saving config:', error);
            return false;
        }
    }

    /**
     * Get specific configuration value using dot notation path
     */
    get(path: string): any {
        const keys = path.split('.');
        let value: any = this.config;

        for (const key of keys) {
            if (value && typeof value === 'object' && key in value) {
                value = value[key];
            } else {
                return undefined;
            }
        }

        return value;
    }

    /**
     * Set specific configuration value using dot notation path
     */
    set(path: string, value: any): boolean {
        const keys = path.split('.');
        let target: any = this.config;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!(key in target) || typeof target[key] !== 'object') {
                target[key] = {};
            }
            target = target[key];
        }

        const lastKey = keys[keys.length - 1];
        target[lastKey] = value;

        this.saveConfig();
        console.log(`✅ Config updated: ${path} = ${value}`);
        return true;
    }

    /**
     * Get the discovery hub URL
     */
    getDiscoveryHubUrl(): string {
        const hub = this.config.discoveryHub;
        const useProxy = this.config.proxy.enabled;

        if (useProxy) {
            return `${hub.url}:${this.config.proxy.port}`;
        } else {
            return `${hub.url}:${hub.port}`;
        }
    }

    /**
     * Get credentials for discovery
     */
    getCredentials(type: string = 'default'): CredentialConfig {
        return this.config.credentials[type] || this.config.credentials.default;
    }

    /**
     * Update discovery hub settings
     */
    updateDiscoveryHub(settings: Partial<DiscoveryHubConfig>): void {
        Object.assign(this.config.discoveryHub, settings);
        this.saveConfig();
        console.log('🔧 Discovery hub settings updated:', settings);
    }

    /**
     * Update proxy settings
     */
    updateProxy(settings: Partial<ProxyConfig>): void {
        Object.assign(this.config.proxy, settings);
        this.saveConfig();
        console.log('🔧 Proxy settings updated:', settings);
    }

    /**
     * Reset to default configuration
     */
    resetToDefault(): void {
        this.config = { ...this.defaultConfig };
        this.saveConfig();
        console.log('🔄 Configuration reset to defaults');
    }

    /**
     * Export configuration as JSON string
     */
    exportConfig(): string {
        return JSON.stringify(this.config, null, 2);
    }

    /**
     * Import configuration from JSON string
     */
    importConfig(jsonString: string): boolean {
        try {
            const newConfig = JSON.parse(jsonString) as Config;
            this.config = newConfig;
            this.saveConfig();
            console.log('📥 Configuration imported successfully');
            return true;
        } catch (error) {
            console.error('Error importing config:', error);
            return false;
        }
    }

    /**
     * Get all configuration
     */
    getAll(): Config {
        return { ...this.config };
    }
}

// Create global instance
declare global {
    interface Window {
        configManager: ConfigManager;
    }
}

window.configManager = new ConfigManager();

console.log('🚀 Configuration Manager initialized');
console.log('📋 Current configuration:', window.configManager.getAll());

// Export for module usage
